import React from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import { FaMale, FaFemale } from "react-icons/fa";
import moment from "moment";
import { ScrollableContent } from "../Styles/InputStyles";
import { ProfileSection, UserInfo } from "../Styles/PageStyles";
import { getLeadRentRange, getLeadSource } from "../../helpers";

const getValueOrDashedIcon = (value) => {
    if (value === null || value === undefined || value === "Invalid date" || value === "") {
        return <span className="text-gray-400">-</span>;
    }
    return value;
};

const getValidDateOrDashedIcon = (date) => {
    if (!date) {
        return <span className="text-gray-400">-</span>;
    }

    const formattedDate = moment(date);

    if (!formattedDate.isValid()) {
        return <span className="text-gray-400">—</span>;
    }

    return formattedDate.format("DD-MMM-YY");
};

const getStatus = (status) => {
    if (status === 1) {
        return <p className="bg-blue-50 text-blue-700 text-xs border-blue-100 border w-24 h-7 flex items-center justify-center rounded-lg">New</p>;
    } else if (status === 2) {
        return <p className="bg-yellow-50 text-yellow-700 text-xs border-yellow-100 border w-24 h-7 flex items-center justify-center rounded-lg">Follow Up</p>;
    } else if (status === 3) {
        return <p className="bg-green-50 text-green-700 text-xs border-green-100 border w-24 h-7 flex items-center justify-center rounded-lg">Visit Scheduled</p>;
    } else if (status === 4) {
        return <p className="bg-purple-50 text-purple-700 text-xs border-purple-100 border w-24 h-7 flex items-center justify-center rounded-lg">Visit Done</p>;
    } else if (status === 5) {
        return <p className="bg-teal-50 text-teal-700 text-xs border-teal-100 border w-24 h-7 flex items-center justify-center rounded-lg">Moved In</p>;
    } else if (status === 6) {
        return <p className="bg-gray-50 text-gray-700 text-xs border-gray-100 border w-24 h-7 flex items-center justify-center rounded-lg">Not Interested</p>;
    } else if (status === 7) {
        return <p className="bg-amber-50 text-amber-700 text-xs border-amber-100 border w-24 h-7 flex items-center justify-center rounded-lg">Contacted</p>;
    } else if (status === 8) {
        return <p className="bg-lime-50 text-lime-700 text-xs border-lime-100 border w-24 h-7 flex items-center justify-center rounded-lg">Interested</p>;
    } else if (status === 9) {
        return <p className="bg-emerald-50 text-emerald-700 text-xs border-emerald-100 border w-24 h-7 flex items-center justify-center rounded-lg">Invalid</p>;
    } else if (status === 10) {
        return <p className="bg-cyan-50 text-cyan-700 text-xs border-cyan-100 border w-24 h-7 flex items-center justify-center rounded-lg">Onhold</p>;
    } else if (status === 11) {
        return <p className="bg-sky-50 text-sky-700 text-xs border-sky-100 border w-24 h-7 flex items-center justify-center rounded-lg">Not Answered</p>;
    } else if (status === 12) {
        return <p className="bg-sky-50 text-red-700 text-xs border-red-100 border w-24 h-7 flex items-center justify-center rounded-lg">Lost</p>;
    } else {
        return <p className="bg-gray-50 text-gray-700 text-xs border-gray-100 border w-24 h-7 flex items-center justify-center rounded-lg">Unknown</p>;
    }
};

const NoteItem = ({ name, time, description }) => {
    time = time ? moment(time).format('DD, MMM YY, hh:mm A') : time;
    const [showFullDescription, setShowFullDescription] = React.useState(false);
    const shouldTruncate = description?.length > 50;
    if (!description)
        return (<div className="bg-white rounded-lg mb-2"></div>);
    return (
        <div className="bg-gray-100 p-3 rounded-lg mb-2">
            <div className="flex justify-between items-center mb-1">
                <span className="text-sm  text-gray-600">By <span className="font-medium text-primary-color">{name}</span></span>
                <span className="text-xs text-gray-600">{time}</span>
            </div>
            <p className="text-sm text-gray-600">
                {showFullDescription ? description : shouldTruncate ? `${description.substring(0, 50)}...` : description}
            </p>
            {shouldTruncate && (
                <button
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    className="text-xs text-blue-500 mt-1 text-right"
                >
                    {showFullDescription ? "Show less" : "Show more"}
                </button>
            )}
        </div>
    );
};

const ViewLead = ({
    setViewDetail,
    selectedData,
    setIsShowEditLead,
}) => {
    const onClose = () => {
        setViewDetail(false);
    };

    const onEditBasicDetails = () => {
        // setViewDetail(false);
        setIsShowEditLead(true);
    };

    return (
        <Container>
            <div className="flex items-center justify-between bg-[#26A69A] py-4 px-4">
                <Title className="text-white">{"Lead Details"}</Title>
                <button onClick={onClose}>
                    <MdClose color="white" size={24} />
                </button>
            </div>

            <ProfileSection className="flex justify-between">
                <UserImageWrapper>
                    {selectedData.gender === 1 ? (
                        <FaMale className="text-gray-400 w-8 h-8" />
                    ) : (
                        <FaFemale className="text-gray-400 w-8 h-8" />
                    )}
                    <div className="flex flex-col gap-1">
                        <span className="text-gray-600 text-sm font-medium">
                            {selectedData?.name?.charAt(0).toUpperCase() + selectedData?.name?.slice(1)}{" "}
                        </span>
                        <span className="text-gray-400 text-xs">#{selectedData.mobile}</span>
                    </div>
                </UserImageWrapper>

                <span>{getStatus(selectedData?.status)}</span>
            </ProfileSection>

            <Content>
                <ScrollableContent>
                    <Wrapper>
                        <BtnWrapper>
                            <SectionTitle>Basic Details</SectionTitle>
                            <EditButton title="Edit Basic Detail" onClick={onEditBasicDetails}>
                                Edit
                            </EditButton>
                        </BtnWrapper>

                        <Section className="mb-3">
                            <Row className="pb-4">
                                <FlexCol>
                                    <Label>Rent Range</Label>
                                    <Value>{getLeadRentRange ? getLeadRentRange(selectedData?.rentRange) : getValueOrDashedIcon(selectedData?.rentRange)}</Value>
                                </FlexCol>
                                <FlexCol>
                                    <Label>Source</Label>
                                    <Value>{getLeadSource ? getLeadSource(selectedData?.source) : getValueOrDashedIcon(selectedData?.source)}</Value>
                                </FlexCol>
                            </Row>
                            <FlexCol className="pb-4">
                                <Label>Visit Date & Time</Label>
                                <Value>
                                    {selectedData?.visitDateTime
                                        ? moment(selectedData.visitDateTime, 'YYYY-MM-DD HH:mm:ss').format('DD, MMM YY, hh:mm A')
                                        : '-'}
                                </Value>

                            </FlexCol>
                            {/* <Row className="pb-4"> */}
                            <FlexCol className="pb-4">
                                <Label>Interested Property</Label>
                                <Value>
                                    {selectedData?.linkedProperties?.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {selectedData.linkedProperties.map((prop, index) => (
                                                <span key={prop.id} className="bg-blue-50 text-primary-color py-1 px-2 rounded-lg">
                                                    {prop.name}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </Value>
                            </FlexCol>
                            <FlexCol className="pb-4">
                                <Label>Assign Staff</Label>
                                <Value>
                                    {selectedData?.linkedStaffs?.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {selectedData.linkedStaffs.map((prop, index) => (
                                                <span key={prop.id} className="bg-blue-50 text-primary-color py-1 px-2 rounded-lg">
                                                    {prop.name}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </Value>
                            </FlexCol>
                            {/* </Row> */}
                        </Section>
                    </Wrapper>
                    {selectedData?.notes?.length > 0 && (
                        <Wrapper>
                            <BtnWrapper>
                                <SectionTitle>Notes</SectionTitle>
                            </BtnWrapper>
                            <FlexCol>
                                {/* <Label>Notes</Label> */}
                                <div >
                                    {selectedData?.notes.map((note, index) => (
                                        <NoteItem
                                            key={index}
                                            name={note.doneByName}
                                            time={note.createdAt}
                                            description={note?.notes || ''}
                                        />
                                    ))}
                                </div>
                            </FlexCol>
                        </Wrapper>)}
                </ScrollableContent>
            </Content>
        </Container>
    );
};

const Wrapper = tw.div`grid gap-1.5 my-0`;
const Container = tw.div`bg-white w-full h-full min-h-[100vh] flex flex-col`;
const Title = tw.h1`text-lg md:text-xl font-medium text-center text-gray-600`;
const Section = tw.div` border border-gray-200 p-3 rounded-lg`;
const SectionTitle = tw.h2`text-base font-medium text-gray-600 flex justify-between items-center`;
const Row = tw.div`grid grid-cols-2 `;
const Label = tw.label`text-sm text-gray-400`;
const Value = tw.span`text-gray-600 text-base`;
const BtnWrapper = tw.div`w-full flex items-center justify-between `;
const FlexCol = tw.div`flex flex-col gap-0.5`
const Content = tw.div`p-4 flex flex-col gap-4`;
const EditButton = tw.button` text-sm px-1 py-0.5 items-center flex justify-between text-primary-color underline`
const UserImageWrapper = tw.div` flex items-center gap-2`;
export default ViewLead;
